/**
 * Setup wizard — 2-step main path with conditional follow-up cards.
 *
 *   Step 1 of 2 — Brand basics (mission + voice + optional website-scan).
 *   Step 2 of 2 — Where do you want to write?
 *     ↳ "Inside Heist"     → BYOK card (provider key, live ping)
 *                            → routes to #create?prefill=sample
 *     ↳ "Right inside your favorite AI chat"
 *                          → platform picker (Claude / ChatGPT / Cursor / Windsurf)
 *                            → each tile deep-links to #connectors with the right context
 *
 * The BYOK step used to live before the fork — that wasted friction for
 * external-chat users who never need a Heist-stored key. Now it only
 * fires when the user actually picks "Inside Heist".
 *
 * Skip from any step → drops a `setup_incomplete` notification linking
 * back to `#?setup=1` so the user can resume from the bell drawer.
 */

import { client } from '../api/client.js';
import { SETUP_VOICE_OPTIONS, BRAND_PROFILE_FIELDS, matchVoiceOption } from '../data/brandProfileSchema.js';
import { MCP_PUBLIC_URL } from '../data/mcpPrompts.js';
import { arrowRight, arrowLeft, check } from './icons.js';
import { track } from '../utils/analytics.js';

const STORAGE_STEP = 'heist.setup.step';
// 'connect' replaced 'platforms' — old value still readable for resume.
const STORAGE_VIEW = 'heist.setup.view'; // 'fork' | 'byok' | 'connect' | 'platforms'(legacy)
// Phase 40 — Step 3 needs to know which finish path the user picked in
// Step 2 so the wizard can re-route correctly on resume. Persisting the
// path key lets us survive a reload mid-Step-3 without losing the user.
const STORAGE_FINISH_PATH = 'heist.setup.finishPath';   // 'mcp' | 'byok' | 'try'
const STORAGE_FINISH_ARGS = 'heist.setup.finishArgs';   // JSON: { platformId?, externalUrl? }
// Phase 41 — set right before a social-connect OAuth redirect. The boot
// orchestrator reads it on the next load to reopen the wizard on the
// channels step, EVEN for users who already completed onboarding (they may
// have opened the wizard manually to add a channel). One-shot.
const STORAGE_RESUME_OAUTH = 'heist.setup.resumeAfterOauth';
// How often the connect view polls /me to detect that the user finished
// pasting the URL into Claude.ai / ChatGPT. Same cadence as the Connectors
// page itself (see connectors.js) so the UX feels consistent.
const MCP_POLL_INTERVAL_MS = 10000;

// Compact, platform-specific instructions shown in the connect view's
// mini-tabbar. Designed to fit on three lines max so the user can read
// them and act in under 30 seconds.
const PLATFORM_STEPS = {
    claude: {
        name: 'Claude.ai',
        sub: 'Pro / Team / Max — fastest path',
        url: 'https://claude.ai/',
        steps: [
            'Click your profile (top-right) → <em>Settings</em> → <em>Connectors</em>',
            'Click <strong>Add custom connector</strong>',
            'Paste the Name + URL below, click Add',
            'Claude opens a small Heist sign-in popup — complete that, and we\'ll detect the connection here automatically',
        ],
    },
    chatgpt: {
        name: 'ChatGPT',
        sub: 'Pro / Business with Connectors enabled',
        url: 'https://chatgpt.com/',
        steps: [
            'Click your profile (bottom-left) → <em>Settings</em> → <em>Connectors</em>',
            'Click <strong>Add</strong>',
            'Paste the Name + URL below, click Add',
            'ChatGPT opens a small Heist sign-in popup — complete that, and we\'ll detect the connection here automatically',
        ],
    },
    cursor: {
        name: 'Cursor',
        sub: 'Desktop app — needs an API key',
        url: 'https://cursor.com/',
        steps: [
            'Cursor uses a different setup (config file + API key)',
            'After this wizard, head to <strong>Settings → Connectors → Advanced</strong>',
            'Or click "Skip for now" below — the desktop setup lives there',
        ],
    },
    windsurf: {
        name: 'Windsurf',
        sub: 'Desktop app — needs an API key',
        url: 'https://windsurf.com/',
        steps: [
            'Windsurf uses a different setup (config file + API key)',
            'After this wizard, head to <strong>Settings → Connectors → Advanced</strong>',
            'Or click "Skip for now" below — the desktop setup lives there',
        ],
    },
};

// VOICE_OPTIONS now imported from brandProfileSchema.js so setupWizard +
// brandWizard share the same option list (with their respective stylings —
// the rich phrases here, the terser single-words for brandWizard).
const VOICE_OPTIONS = SETUP_VOICE_OPTIONS;

// Real platform icons via Google's favicon CDN (full-color, ~64px PNG).
// Each platform's official mark is served from
// www.google.com/s2/favicons — reliable, no API key, CSP-allowed under
// img-src https:. The img is loaded async with `loading="lazy"` since
// the picker is below the fold on first paint.
function platformIcon(domain, alt) {
    return `<img src="https://www.google.com/s2/favicons?domain=${domain}&sz=64" alt="${alt}" width="22" height="22" loading="lazy" />`;
}
const PLATFORM_ICONS = {
    claude:   platformIcon('claude.ai', 'Claude'),
    chatgpt:  platformIcon('chatgpt.com', 'ChatGPT'),
    cursor:   platformIcon('cursor.com', 'Cursor'),
    windsurf: platformIcon('windsurf.com', 'Windsurf'),
    copilot:  platformIcon('code.visualstudio.com', 'VS Code'),
};

// Each platform tile opens the AI chat directly in a new tab. The Heist
// tab stays on /#connectors so the user has the OAuth / API-key setup
// ready in the original window. Connector docs are linked from the
// prompt card's instructions, not the tiles themselves.
const PLATFORMS = [
    { id: 'claude',   name: 'Claude.ai',  sub: 'Browser sign-in — fastest path',    url: 'https://claude.ai/'             },
    { id: 'chatgpt',  name: 'ChatGPT',    sub: 'Browser sign-in — Pro / Business',  url: 'https://chatgpt.com/'           },
    { id: 'cursor',   name: 'Cursor',     sub: 'Desktop app — needs an API key',    url: 'https://cursor.com/'            },
    { id: 'windsurf', name: 'Windsurf',   sub: 'Desktop app — needs an API key',    url: 'https://windsurf.com/'          },
    { id: 'copilot',  name: 'Copilot (VS Code)', sub: 'VS Code — needs an API key',  url: 'https://code.visualstudio.com/' },
];

// Phase 41 — "Connect your channels" grid on Step 2. These are the publish
// destinations (where finished content goes), distinct from the AI-chat
// connection (how you ask Heist to make it). Each tile reuses the SAME
// OAuth client methods the Settings → Integrations page uses, so there's one
// connection implementation. Provider ids match the /integrations payload
// (`item.provider`) so we can show a live ✓ once connected.
const SOCIAL_PLATFORMS = [
    { id: 'meta',      name: 'Instagram & Facebook', domain: 'facebook.com'  },
    { id: 'linkedin',  name: 'LinkedIn',             domain: 'linkedin.com'  },
    { id: 'tiktok',    name: 'TikTok',               domain: 'tiktok.com'    },
    { id: 'youtube',   name: 'YouTube',              domain: 'youtube.com'   },
    { id: 'pinterest', name: 'Pinterest',            domain: 'pinterest.com' },
    { id: 'reddit',    name: 'Reddit',               domain: 'reddit.com'    },
    { id: 'wordpress', name: 'WordPress',            domain: 'wordpress.com' },
];

// Maps a social provider id to the client call that returns its OAuth URL.
// WordPress is special — it needs the user's blog URL first. Returning null
// signals "ask for the blog URL before calling."
function socialOAuthStart(provider) {
    switch (provider) {
        case 'meta':      return client.getMetaOAuthUrl();
        case 'linkedin':  return client.getLinkedInOAuthUrl();
        case 'tiktok':    return client.connectTikTok();
        case 'youtube':   return client.connectYouTube();
        case 'pinterest': return client.connectPinterest();
        case 'reddit':    return client.connectReddit();
        default:          return null;
    }
}

let overlay = null;
let state = null;

function persistStep(idx) {
    try { localStorage.setItem(STORAGE_STEP, String(idx)); } catch (_) {}
}
function persistView(view) {
    try { view ? localStorage.setItem(STORAGE_VIEW, view) : localStorage.removeItem(STORAGE_VIEW); } catch (_) {}
}
function readStep() {
    try {
        // Phase 40 — Step 3 was added at index 2; valid range is now [0, 2].
        const n = parseInt(localStorage.getItem(STORAGE_STEP) || '0', 10);
        return Number.isFinite(n) && n >= 0 && n < 3 ? n : 0;
    } catch (_) { return 0; }
}
function readView() {
    try { return localStorage.getItem(STORAGE_VIEW) || 'fork'; } catch (_) { return 'fork'; }
}
function clearAll() {
    try {
        localStorage.removeItem(STORAGE_STEP);
        localStorage.removeItem(STORAGE_VIEW);
        // Phase 40 — also clear the Step-3 finish-path stash so a future
        // signup on this device starts from a clean slate.
        localStorage.removeItem(STORAGE_FINISH_PATH);
        localStorage.removeItem(STORAGE_FINISH_ARGS);
        localStorage.removeItem(STORAGE_RESUME_OAUTH);
    } catch (_) {}
}

function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/**
 * Public entry. opts.resume = true to start at the persisted step + view.
 */
export function startSetupWizard(opts = {}) {
    if (overlay) return; // already open
    let resumedView = opts.resume ? readView() : 'fork';
    // Migrate the legacy 'platforms' value from earlier builds — the view
    // was renamed when the platform picker was replaced with the inline
    // URL-paste card.
    if (resumedView === 'platforms') resumedView = 'connect';
    state = {
        step: opts.resume ? readStep() : 0,
        view: resumedView, // step-2 sub-view: 'fork' | 'byok' | 'connect'
        scanning: false,
        scanError: null,
        savingProfile: false,
        savingKey: false,
        keyError: null,
        keyOk: false,
        provider: 'anthropic',
        apiKey: '',
        brandName: '',
        mission: '',
        voice: VOICE_OPTIONS[0],
        voiceCustom: '',
        // Connect-view state: which platform's instructions are shown,
        // whether the live MCP-detection poll is running, whether we've
        // detected a successful connection.
        selectedPlatform: 'claude',
        mcpPolling: false,
        mcpDetected: !!(window.__auth__ && window.__auth__.mcp_connected),
        // Phase 41 — "Connect your channels" social grid on Step 2. `null`
        // until the first /integrations fetch resolves; then an array of
        // provider ids whose status === 'connected'. Lets the grid flip a
        // tile to a ✓ after the OAuth round-trip reopens the wizard.
        connectedProviders: null,
        loadingIntegrations: false,
        integrationsError: null,
    };
    mount();
    render();
}

function mount() {
    overlay = document.createElement('div');
    overlay.className = 'setup-wizard-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    document.body.appendChild(overlay);
    overlay.addEventListener('click', onClick);
    overlay.addEventListener('input', onInput);
    overlay.addEventListener('change', onChange);
}

function unmount() {
    stopMcpPoll();
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
    overlay = null;
    state = null;
}

function render() {
    if (!overlay) return;
    persistStep(state.step);
    // Decide BEFORE the HTML is built whether the connect-view pill should
    // render in its polling state — otherwise the first paint shows the
    // idle copy and the pulse only appears after the next render tick.
    const shouldPoll = state.step === 1 && state.view === 'connect' && !state.mcpDetected;
    state.mcpPolling = shouldPoll;
    if (state.step === 0) overlay.innerHTML = renderStep1();
    else if (state.step === 2) overlay.innerHTML = renderStep3();
    else {
        // Step 2 is one of three sub-views.
        if (state.view === 'byok') overlay.innerHTML = renderByok();
        else if (state.view === 'connect') overlay.innerHTML = renderConnect();
        else overlay.innerHTML = renderFork();
    }
    const firstInput = overlay.querySelector('input[type=text], textarea, input[type=password]');
    if (firstInput) firstInput.focus();
    // Start/stop the actual setInterval. The poll only matters while the
    // user is staring at the connect view — leaving it running on other
    // views would burn /me requests for nothing.
    if (shouldPoll) startMcpPoll();
    else stopMcpPoll();
    // Phase 41 — lazily load connection status the first time the channels
    // grid (fork view) is shown, so each tile can render its ✓ state.
    if (state.step === 1 && state.view === 'fork' && state.connectedProviders === null && !state.loadingIntegrations) {
        fetchConnectedProviders();
    }
}

// Phase 41 — fetch which publish channels are already connected. Reuses the
// same /integrations payload the Settings page uses (status === 'connected').
async function fetchConnectedProviders() {
    state.loadingIntegrations = true;
    try {
        const res = await client.getIntegrations();
        const items = (res && res.items) || [];
        state.connectedProviders = items.filter(i => i && i.status === 'connected').map(i => i.provider);
    } catch (_) {
        state.connectedProviders = []; // degrade gracefully — tiles just show "Connect"
    }
    state.loadingIntegrations = false;
    // Only re-render if the user is still on the fork view.
    if (overlay && state.step === 1 && state.view === 'fork') render();
}

// Phase 41 — start a social platform OAuth from the channels grid. Mirrors
// the Settings → Integrations connect flow exactly: fetch the provider's
// OAuth URL, then redirect the whole page. WordPress needs the blog URL
// first. The wizard state is already persisted (step + view='fork'), and the
// orchestrator reopens the wizard after the redirect returns.
async function connectSocial(btn) {
    const provider = btn && btn.dataset.provider;
    if (!provider) return;
    const original = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span class="sw-social-tile__name">Redirecting…</span>';
    try {
        let res;
        if (provider === 'wordpress') {
            const blog = window.prompt('Enter your WordPress.com site URL (e.g. mysite.wordpress.com):');
            if (!blog) { btn.disabled = false; btn.innerHTML = original; return; }
            res = await client.connectWordPressCom(blog.trim());
        } else {
            res = await socialOAuthStart(provider);
        }
        if (res && res.url) {
            // Persist where we are so the post-redirect reopen lands here, and
            // flag the resume so the orchestrator reopens the wizard even for
            // users who already finished onboarding.
            persistStep(1); persistView('fork');
            try { localStorage.setItem(STORAGE_RESUME_OAUTH, '1'); } catch (_) {}
            window.location.href = res.url;
            return;
        }
        // No URL → the provider isn't configured on this deployment.
        btn.disabled = false;
        btn.innerHTML = original;
        state.integrationsError = `${provider} sign-in isn't available yet — you can connect it later in Settings.`;
        render();
    } catch (_) {
        btn.disabled = false;
        btn.innerHTML = original;
        state.integrationsError = `Couldn't start ${provider} sign-in. Try again, or connect it later in Settings.`;
        render();
    }
}

let mcpPollHandle = null;
function startMcpPoll() {
    if (mcpPollHandle) return;
    state.mcpPolling = true;
    mcpPollHandle = setInterval(async () => {
        if (!state) return;
        try {
            const me = await client.authMe();
            const connected = !!(me && me.user && me.user.mcp_connected);
            if (connected) {
                if (window.__auth__) window.__auth__.mcp_connected = true;
                state.mcpDetected = true;
                state.mcpPolling = false;
                stopMcpPoll();
                // Re-render so the success block replaces the URL-paste card.
                if (overlay) render();
                try { window.dispatchEvent(new CustomEvent('heist:mcp-connected')); } catch (_) {}
            }
        } catch (_) {}
    }, MCP_POLL_INTERVAL_MS);
}
function stopMcpPoll() {
    if (mcpPollHandle) { clearInterval(mcpPollHandle); mcpPollHandle = null; }
    if (state) state.mcpPolling = false;
}

function progressDots(active) {
    // Phase 40 — three dots now (brand → connect → what you can make).
    return `<div class="sw-progress">
      ${[0, 1, 2].map(i => `<span class="sw-dot ${i === active ? 'is-active' : i < active ? 'is-done' : ''}"></span>`).join('')}
    </div>`;
}

function renderStep1() {
    const { brandName, mission, voice, voiceCustom, scanning, scanError } = state;
    return `
      <div class="h-pane setup-wizard-pane">
        ${progressDots(0)}
        <div class="h-pane-head">
          <div class="h-eyebrow">Step 1 of 3</div>
          <h2 class="h-title">Tell Heist about your brand</h2>
          <p class="h-lede">Brief the crew on who you are. Just a few things to start — you can edit everything later in The Brain.</p>
        </div>
        <div class="h-field">
          <label class="h-label">Brand name — what should we call your workspace?</label>
          <input class="h-input" data-field="brandName" type="text" placeholder="e.g. Acme Coffee Co." value="${escapeHtml(brandName)}" />
        </div>
        <div class="h-field">
          <label class="h-label">Mission — what does your brand do, and for who?</label>
          <textarea class="h-textarea" data-field="mission" placeholder="e.g. We help small businesses grow with affordable marketing tools.">${escapeHtml(mission)}</textarea>
        </div>
        <div class="h-field">
          <label class="h-label">Voice — how should posts sound?</label>
          <select class="h-select" data-field="voice">
            ${VOICE_OPTIONS.map(v => `<option value="${escapeHtml(v)}" ${v === voice ? 'selected' : ''}>${escapeHtml(v)}</option>`).join('')}
          </select>
          ${voice === 'Other' ? `<input class="h-input" data-field="voiceCustom" type="text" placeholder="e.g. Sarcastic, Gen-Z, Corporate-but-fun" value="${escapeHtml(voiceCustom)}" style="margin-top:8px;" />` : ''}
        </div>
        <div class="h-field">
          <label class="h-label">Have a website? Auto-fill mission + voice from it</label>
          <div class="sw-scan__row">
            <input class="h-input" data-field="scanUrl" type="url" placeholder="https://yourwebsite.com" />
            <button class="h-btn h-btn--ghost" data-action="scan-website" ${scanning ? 'disabled' : ''}>${scanning ? 'Scanning…' : 'Scan'}</button>
          </div>
          ${scanError ? `<div class="h-callout h-callout--error" style="margin-top:8px;">${escapeHtml(scanError)}</div>` : ''}
        </div>
        <div class="sw-divider"><span>or</span></div>
        <button class="sw-alt-card" data-action="fill-via-chat" type="button">
          <span class="sw-alt-card__icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          </span>
          <span class="sw-alt-card__body">
            <span class="sw-alt-card__title">Let your AI chat fill this in for you</span>
            <span class="sw-alt-card__sub">Connect Claude / ChatGPT / Cursor / Copilot and answer a few questions — it'll save your brand to Heist as you go.</span>
          </span>
          <span class="sw-alt-card__cta">Continue ${arrowRight()}</span>
        </button>
        <div class="sw-actions">
          <button class="h-btn h-btn--ghost" data-action="skip">Skip setup</button>
          <button class="h-btn h-btn--primary" data-action="next-1" ${(!brandName || !mission || !voice) ? 'disabled' : ''}>Continue ${arrowRight()}</button>
        </div>
      </div>
    `;
}

// Phase 41 — the social grid for Step 2's "Where you'll publish" group.
// Each tile reuses the Settings OAuth client methods (see socialOAuthStart),
// so there's one connection implementation. Connecting is a full-page OAuth
// redirect (same as Settings); the orchestrator reopens this wizard on the
// next boot because onboarding isn't complete yet, so the user lands back
// here with the tile flipped to ✓.
function renderSocialGrid() {
    const connected = state.connectedProviders || [];
    const loading = state.loadingIntegrations && state.connectedProviders === null;
    const tiles = SOCIAL_PLATFORMS.map(p => {
        const isOn = connected.includes(p.id);
        return `
          <button type="button" class="sw-social-tile ${isOn ? 'is-connected' : ''}" data-action="connect-social" data-provider="${escapeHtml(p.id)}" ${isOn ? 'disabled' : ''}>
            <span class="sw-social-tile__icon">${platformIcon(p.domain, p.name)}</span>
            <span class="sw-social-tile__name">${escapeHtml(p.name)}</span>
            <span class="sw-social-tile__status">${isOn ? `${check({ size: 14, strokeWidth: 2.6 })} Connected` : 'Connect'}</span>
          </button>`;
    }).join('');
    return `
      <div class="sw-social">
        <div class="sw-social__grid ${loading ? 'is-loading' : ''}">${tiles}</div>
        ${state.integrationsError ? `<div class="h-callout h-callout--error" style="margin-top:8px;">${escapeHtml(state.integrationsError)}</div>` : ''}
        <div class="sw-social__hint">Connecting opens a one-time sign-in, then brings you right back here. You can connect more later in <strong>Settings → Integrations</strong> — or just skip this and do it after setup.</div>
      </div>`;
}

function renderFork() {
    // Phase 41 — Step 2 now leads with the publish channels (the product's
    // actual payoff) and keeps the AI-chat connection as the "how you'll
    // work" group beneath it. The MCP card stays "Recommended". BYOK +
    // built-in chat remain reachable from the small link + Settings → AI.
    return `
      <div class="h-pane setup-wizard-pane setup-wizard-pane--wide">
        ${progressDots(1)}
        <div class="h-pane-head">
          <div class="h-eyebrow">Step 2 of 3</div>
          <h2 class="h-title">Connect your channels</h2>
          <p class="h-lede">Line up the getaway routes — hook up the accounts Heist will publish to, and the AI chat you'll work from. Both are optional now; you can add either later.</p>
        </div>

        <div class="sw-group">
          <div class="sw-group__label">Where you'll publish</div>
          ${renderSocialGrid()}
        </div>

        <div class="sw-group">
          <div class="sw-group__label">How you'll work with Heist</div>
          <div class="sw-fork sw-fork--stacked">
            <button class="sw-fork__card sw-fork__card--featured" data-action="fork-mcp">
              <div class="sw-fork__badge">Recommended</div>
              <div class="sw-fork__emoji"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>
              <h3>Use my AI chat (Claude, ChatGPT, Cursor…)</h3>
              <p>Stay in the AI chat you already love. Ask for posts, captions, or campaign plans there — drafts land in Heist for you to review and publish. No API keys needed for browser sign-in.</p>
              <div class="sw-fork__cta">Show me how ${arrowRight()}</div>
            </button>
          </div>
          <div class="sw-social__hint">Heads up: you connect publishing accounts here in Heist, not from chat. Once they're connected, your AI chat can post to them for you.</div>
        </div>

        <div class="sw-actions">
          <button class="h-btn h-btn--ghost" data-action="back">${arrowLeft()} Back</button>
          <div class="sw-fork__alt">
            <a href="#" data-action="skip-to-builtin" class="sw-fork__alt-link">Skip for now — use Heist's built-in chat</a>
            <div class="sw-fork__alt-hint">Power user? You can paste your own OpenAI / Anthropic / Google key in <strong>Settings → AI</strong> later.</div>
          </div>
        </div>
      </div>
    `;
}

function renderByok() {
    const { provider, apiKey, savingKey, keyError, keyOk } = state;
    const placeholders = { openai: 'sk-…', anthropic: 'sk-ant-…', google: 'AIza…' };
    const hints = {
        openai: { name: 'OpenAI', url: 'platform.openai.com/api-keys' },
        anthropic: { name: 'Anthropic', url: 'console.anthropic.com/settings/keys' },
        google: { name: 'Google AI Studio', url: 'aistudio.google.com/app/apikey' },
    };
    const h = hints[provider];
    return `
      <div class="h-pane setup-wizard-pane">
        ${progressDots(1)}
        <div class="h-pane-head">
          <div class="h-eyebrow">Step 2 of 3</div>
          <h2 class="h-title">Connect your AI</h2>
          <p class="h-lede">Paste an API key so Heist's writer can use your model directly. You pay your provider at cost — no markup. We'll test it before saving; you can skip and add one later in Settings.</p>
        </div>
        <div class="h-field">
          <label class="h-label">Provider</label>
          <select class="h-select" data-field="provider">
            <option value="anthropic" ${provider === 'anthropic' ? 'selected' : ''}>Anthropic — Claude (recommended for writing)</option>
            <option value="openai" ${provider === 'openai' ? 'selected' : ''}>OpenAI — GPT</option>
            <option value="google" ${provider === 'google' ? 'selected' : ''}>Google — Gemini</option>
          </select>
        </div>
        <div class="h-field">
          <label class="h-label">API key</label>
          <input class="h-input" type="password" data-field="apiKey" placeholder="${placeholders[provider] || ''}" value="${escapeHtml(apiKey)}" autocomplete="off" spellcheck="false" />
          <div class="h-help">Get one at <a href="https://${h.url}" target="_blank" rel="noopener noreferrer">${h.url}</a> — <strong>not</strong> your Claude.ai / ChatGPT login or Desktop-app session.</div>
        </div>
        ${keyError ? `<div class="h-callout h-callout--error">${escapeHtml(keyError)}</div>` : ''}
        ${keyOk ? `<div class="h-callout h-callout--success"><span class="h-callout__icon">${check()}</span>Connected to ${escapeHtml(h.name)}</div>` : ''}
        <div class="sw-actions">
          <button class="h-btn h-btn--ghost" data-action="back-to-fork">${arrowLeft()} Back</button>
          <div class="sw-actions__right">
            <button class="h-btn h-btn--ghost" data-action="skip-key-finish">Skip & open Studio</button>
            <button class="h-btn h-btn--primary" data-action="save-key" ${savingKey || !apiKey ? 'disabled' : ''}>${savingKey ? 'Testing…' : keyOk ? `Continue ${arrowRight()}` : 'Test & save'}</button>
          </div>
        </div>
      </div>
    `;
}

function renderConnect() {
    // Used to be a platform picker that bounced the user to /#connectors
    // mid-wizard. Now we inline everything they need: the literal Name + URL
    // they have to paste, a per-platform 3-step recipe via mini-tabs, a
    // live "Detecting…" pill that flips to "✓ Connected" via the /me poll,
    // and a single CTA that opens their chosen AI chat in a new tab.
    // Always show the production URL — that's the only address external
    // AI chats (Claude.ai, ChatGPT, Cursor, Windsurf) can reach. Local
    // dev sessions used to show `localhost:8085/mcp` which never worked
    // when pasted into a real AI chat.
    const remoteMcpUrl = MCP_PUBLIC_URL;
    const platformId = state.selectedPlatform || 'claude';
    const p = PLATFORM_STEPS[platformId] || PLATFORM_STEPS.claude;
    const tabs = ['claude', 'chatgpt', 'cursor', 'windsurf'].map(id => {
        const def = PLATFORM_STEPS[id];
        const sel = id === platformId;
        return `<button class="sw-conn__tab ${sel ? 'is-active' : ''}" data-action="pick-platform" data-platform="${escapeHtml(id)}">
          <span class="sw-conn__tab-icon">${PLATFORM_ICONS[id] || ''}</span>
          <span class="sw-conn__tab-name">${escapeHtml(def.name)}</span>
        </button>`;
    }).join('');

    if (state.mcpDetected) {
        // Success view — replaces the URL/Name fields once we detect that
        // the user has finished pasting everything into their AI chat.
        return `
          <div class="h-pane setup-wizard-pane setup-wizard-pane--wide">
            ${progressDots(1)}
            <div class="h-pane-head">
              <div class="h-eyebrow">Step 2 of 3</div>
              <h2 class="h-title"><span class="h-title__icon">${check({ size: 22, strokeWidth: 2.6 })}</span>Connected to your AI chat</h2>
              <p class="h-lede">Heist is talking to your AI chat. Open it and try a prompt — drafts will land in your Heist inbox.</p>
            </div>
            <div class="sw-conn__success">
              <p>One thing to try first: paste this into your AI chat to let it walk you through filling in your brand profile (mission, voice, audience). It's optional, but most people find it the fastest way to finish setup.</p>
              <div class="sw-conn__prompt">
                <code>Help me set up my brand profile in Heist. Ask me one question at a time about my mission, voice, audience, goals, and offers. If I don't know an answer, suggest options based on what we've discussed so far. I can also share my website URL or upload brand documents — confirm with me before saving each piece.</code>
                <button class="h-btn h-btn--ghost" data-action="copy-prompt">Copy prompt</button>
              </div>
            </div>
            <div class="sw-actions">
              <button class="h-btn h-btn--ghost" data-action="back-to-fork">${arrowLeft()} Back</button>
              <div class="sw-actions__right">
                <button class="h-btn h-btn--ghost" data-action="finish-mcp-no-open">I'll come back later</button>
                <button class="h-btn h-btn--primary" data-action="finish-mcp-open" data-url="${escapeHtml(p.url)}">Open ${escapeHtml(p.name)} ${arrowRight()}</button>
              </div>
            </div>
          </div>
        `;
    }

    return `
      <div class="h-pane setup-wizard-pane setup-wizard-pane--wide">
        ${progressDots(1)}
        <div class="h-pane-head">
          <div class="h-eyebrow">Step 2 of 3</div>
          <h2 class="h-title">Connect Heist to your AI chat</h2>
          <p class="h-lede">Pick the chat you use, paste the URL into its Connectors settings — Heist detects the connection and we'll move on automatically.</p>
        </div>

        <div class="sw-conn">
          <div class="sw-conn__tabs" role="tablist">${tabs}</div>
          <div class="sw-conn__sub">${escapeHtml(p.sub)}</div>
          <ol class="sw-conn__steps">
            <li class="sw-conn__step-open">
              Open <strong>${escapeHtml(p.name)}</strong> in a separate window so you can follow these steps side-by-side.
              <button class="h-btn h-btn--ghost h-btn--sm sw-conn__open-inline" data-action="open-platform" data-url="${escapeHtml(p.url)}">Open ${escapeHtml(p.name)} in a new tab</button>
            </li>
            ${p.steps.map(s => `<li>${s}</li>`).join('')}
          </ol>

          <div class="sw-conn__field">
            <div class="sw-conn__field-label">Name</div>
            <div class="sw-conn__field-row">
              <code class="sw-conn__field-value">Heist</code>
              <button class="h-btn h-btn--ghost h-btn--sm" data-action="copy-text" data-copy="Heist">Copy</button>
            </div>
          </div>
          <div class="sw-conn__field">
            <div class="sw-conn__field-label">URL <span class="sw-conn__field-must">paste exactly</span></div>
            <div class="sw-conn__field-row">
              <code class="sw-conn__field-value">${escapeHtml(remoteMcpUrl)}</code>
              <button class="h-btn h-btn--primary h-btn--sm" data-action="copy-text" data-copy="${escapeHtml(remoteMcpUrl)}">Copy URL</button>
            </div>
          </div>

          <div class="sw-conn__pill ${state.mcpPolling ? 'is-polling' : 'is-idle'}">
            <span class="sw-conn__pill-dot"></span>
            ${state.mcpPolling
              ? `Watching for your connection… we'll move on the moment your AI chat hits Heist.`
              : `Not connected yet — paste the URL above into your AI chat and we'll detect it.`}
          </div>
        </div>

        <div class="sw-actions">
          <button class="h-btn h-btn--ghost" data-action="back-to-fork">${arrowLeft()} Back</button>
          <div class="sw-actions__right">
            <button class="h-btn h-btn--ghost" data-action="skip-platforms-finish">I'll do this later</button>
            <button class="h-btn h-btn--primary" data-action="open-platform" data-url="${escapeHtml(p.url)}">Open ${escapeHtml(p.name)} ${arrowRight()}</button>
          </div>
        </div>
      </div>
    `;
}


// Phase 40 — Step 3 "What you can make".
//
// Beginner-friendly intro to the video + image flows that shipped in
// Phases 37 / 37.5 / 38 / 39. The two cards each show:
//   1. A copy-pasteable prompt for the user's AI chat (MCP-first).
//   2. The in-app click-path as a smaller "or" alternative.
//
// The MCP explainer line at the bottom is shown ONCE here — it's also
// surfaced separately in the help center for users who skip the wizard.
function renderStep3() {
    return `
      <div class="h-pane setup-wizard-pane setup-wizard-pane--wide">
        ${progressDots(2)}
        <div class="h-pane-head">
          <div class="h-eyebrow">Step 3 of 3</div>
          <h2 class="h-title">What you can make</h2>
          <p class="h-lede">The job's set up — here's the score. Two things Heist does that most people miss on day one. Both work from your AI chat OR from inside Heist — pick the path you like.</p>
        </div>

        <div class="sw-uses-grid">
          <section class="sw-uses-card">
            <header class="sw-uses-card__head">
              <span class="sw-uses-card__emoji" aria-hidden="true">🎬</span>
              <h3 class="sw-uses-card__title">Make a faceless video</h3>
            </header>
            <p class="sw-uses-card__sub">Script → AI voice → slideshow MP4, ready for TikTok / Reels / Shorts. Render in ~90 seconds.</p>

            <div class="sw-uses-card__primary">
              <div class="sw-uses-card__primary-label">From your AI chat</div>
              <div class="sw-uses-card__prompt">
                <code>Hey Heist, write a 60-second TikTok script about [your topic] and turn it into a faceless video. Use my brand voice.</code>
                <button type="button" class="h-btn h-btn--ghost h-btn--sm" data-action="copy-text" data-copy="Hey Heist, write a 60-second TikTok script about [your topic] and turn it into a faceless video. Use my brand voice.">Copy</button>
              </div>
            </div>

            <div class="sw-uses-card__alt">
              <span class="sw-uses-card__alt-label">Or in Heist</span>
              <span class="sw-uses-card__alt-path">Content Studio → <strong>Faceless Video</strong> tab → paste a script → click Generate.</span>
            </div>

            <a href="#help?tab=guides&q=faceless video" class="sw-uses-card__more">Learn more about videos ${arrowRight()}</a>
          </section>

          <section class="sw-uses-card" data-testid="sw-step3-image-card">
            <header class="sw-uses-card__head">
              <span class="sw-uses-card__emoji" aria-hidden="true">🖼</span>
              <h3 class="sw-uses-card__title">Add AI images</h3>
            </header>
            <p class="sw-uses-card__sub">Editorial cover images for blogs, on-brand images for social posts. Or import the one your AI chat already made.</p>

            <div class="sw-uses-card__primary">
              <div class="sw-uses-card__primary-label">From your AI chat</div>
              <div class="sw-uses-card__prompt">
                <code>Heist, generate a cinematic featured image for my latest blog post and attach it.</code>
                <button type="button" class="h-btn h-btn--ghost h-btn--sm" data-action="copy-text" data-copy="Heist, generate a cinematic featured image for my latest blog post and attach it.">Copy</button>
              </div>
              <div class="sw-uses-card__prompt sw-uses-card__prompt--alt" data-testid="sw-step3-image-from-chat">
                <code>Take the image I just generated in this chat and add it to my latest Instagram draft.</code>
                <button type="button" class="h-btn h-btn--ghost h-btn--sm" data-action="copy-text" data-copy="Take the image I just generated in this chat and add it to my latest Instagram draft.">Copy</button>
              </div>
            </div>

            <div class="sw-uses-card__alt">
              <span class="sw-uses-card__alt-label">Or in Heist</span>
              <span class="sw-uses-card__alt-path">Blog editor → Featured Image → <strong>Generate with AI</strong>. Social Studio has a similar button next to each post. Media library modal has a "Generate AI image" section near the top.</span>
            </div>

            <a href="#help?tab=guides&q=image" class="sw-uses-card__more">Learn more about images ${arrowRight()}</a>
          </section>
        </div>

        <div class="sw-explainer">
          <strong>About MCP:</strong> the way your AI chat (Claude, ChatGPT, Cursor) plugs into Heist. Once you set it up in Step 2, you can ask it to do anything Heist can do from chat — make posts, write blogs, generate videos, schedule everything. The in-app buttons are always there too if you prefer clicking.
        </div>

        <div class="sw-actions">
          <button class="h-btn h-btn--ghost" data-action="finish-step3">Got it</button>
          <div class="sw-actions__right">
            <button class="h-btn h-btn--ghost" data-action="finish-step3-calendar">Plan my month</button>
            <button class="h-btn h-btn--primary" data-action="finish-step3-create">Make my first post ${arrowRight()}</button>
          </div>
        </div>
      </div>
    `;
}

function onClick(e) {
    // Some new actions live inside <a> tags whose default navigation would
    // hash-change away from the wizard before we can act. Prevent default
    // for any data-action click so the wizard owns navigation.
    const btn = e.target.closest('[data-action]');
    if (!btn) return;
    if (btn.tagName === 'A') e.preventDefault();
    const action = btn.dataset.action;
    if (action === 'skip') return handleSkip();
    if (action === 'back') { state.step = Math.max(0, state.step - 1); state.view = 'fork'; persistView(null); render(); return; }
    if (action === 'back-to-fork') { state.view = 'fork'; persistView(null); render(); return; }
    if (action === 'next-1') return advanceFromStep1();
    if (action === 'fork-heist') { state.view = 'byok'; persistView('byok'); render(); return; }
    if (action === 'fork-mcp')   { state.view = 'connect'; persistView('connect'); render(); return; }
    // The old "Just let me try it" + "Bring my own AI key" cards collapsed
    // into one small "Skip for now — use Heist's built-in chat" link.
    // Routes the user to the in-app chat with a free trial-credits seed.
    if (action === 'skip-to-builtin') return finishJustTryPath();
    // Phase 41 — a "Where you'll publish" social tile. Kicks off the same
    // full-page OAuth redirect Settings uses. The orchestrator reopens this
    // wizard on the next boot (onboarding still incomplete), landing the user
    // back on the channels grid with the tile flipped to ✓.
    if (action === 'connect-social') return connectSocial(btn);
    // "Let your AI chat fill this in" alt-card on Step 1 jumps straight
    // to the connect view so the user can paste the URL right away. The
    // post-connection success view shows a copyable starter prompt that
    // walks them through brand setup via MCP tools.
    if (action === 'fill-via-chat') {
        state.step = 1; state.view = 'connect';
        persistView('connect'); render();
        return;
    }
    // Connect view: pick which platform's instructions to show.
    if (action === 'pick-platform') {
        state.selectedPlatform = btn.dataset.platform || 'claude';
        render();
        return;
    }
    // Generic copy-to-clipboard for the URL/Name fields and the starter
    // prompt. Brief flash on the button so the user sees the click landed.
    if (action === 'copy-text') {
        const text = btn.dataset.copy || '';
        copyToClipboard(text, btn);
        return;
    }
    if (action === 'copy-prompt') {
        const code = btn.parentElement && btn.parentElement.querySelector('code');
        if (code) copyToClipboard(code.textContent || '', btn);
        return;
    }
    // Open the chosen AI chat in a new tab without finishing the wizard —
    // the poll keeps watching, and the wizard auto-advances to the success
    // view once /me reports mcp_connected.
    if (action === 'open-platform') {
        try { window.open(btn.dataset.url || '', '_blank', 'noopener'); } catch (_) {}
        return;
    }
    // Pre-success path: user wants to bail without connecting. Same as the
    // old "skip-platforms-finish" — finalize with the MCP path so the user
    // lands on /#connectors and can finish there at their own pace.
    if (action === 'skip-platforms-finish') return finishMcpPath(state.selectedPlatform);
    // Post-success paths.
    if (action === 'finish-mcp-no-open') return finishMcpPath(state.selectedPlatform);
    if (action === 'finish-mcp-open') return finishMcpPath(state.selectedPlatform, btn.dataset.url);
    if (action === 'save-key') return saveKey();
    if (action === 'skip-key-finish') return finishHeistPath();
    if (action === 'scan-website') return scanWebsite();
    // Phase 40/41 — Step 3 "What you can make" terminal actions.
    if (action === 'finish-step3') return finishStep3();
    if (action === 'finish-step3-create') return finishStep3('#create?prefill=sample');
    if (action === 'finish-step3-calendar') return finishStep3('#calendar');
}

function copyToClipboard(text, btn) {
    // innerHTML so the inline SVG check icon renders as markup, not as
    // a literal "<svg ...>...</svg>" string. Original innerHTML is
    // captured so the button's content (icon + label) restores cleanly
    // after the flash.
    const original = btn.innerHTML;
    const flash = (markup) => {
        btn.innerHTML = markup;
        setTimeout(() => { btn.innerHTML = original; }, 1200);
    };
    const okMarkup = `<span class="i-svg-inline">${check()}</span> Copied`;
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(() => flash(okMarkup), () => flash('Copy failed'));
            return;
        }
    } catch (_) { /* fall through */ }
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        flash(okMarkup);
    } catch (_) {
        flash('Copy failed');
    }
}

function onInput(e) {
    const field = e.target.dataset.field;
    if (!field) return;
    state[field] = e.target.value;
    if (field === 'voice') render();
    if (field === 'brandName' || field === 'mission' || field === 'voiceCustom' || field === 'apiKey') {
        const next = overlay.querySelector('[data-action="next-1"], [data-action="save-key"]');
        if (next) next.disabled = (state.step === 0 ? !state.brandName || !state.mission || !state.voice : !state.apiKey);
    }
}

function onChange(e) {
    const field = e.target.dataset.field;
    if (field === 'voice' || field === 'provider') {
        state[field] = e.target.value;
        render();
    }
}

async function scanWebsite() {
    const urlInput = overlay.querySelector('[data-field="scanUrl"]');
    const url = urlInput && urlInput.value.trim();
    if (!url) { state.scanError = 'Add a URL first.'; render(); return; }
    state.scanning = true;
    state.scanError = null;
    render();
    try {
        const res = await client.suggestProfile(url);
        // Server (POST /api/memory/suggest-profile) returns { ok, suggestions }.
        // The legacy `res.suggested` (singular) fallback was a code-smell from
        // when this and brandWizard.js drifted — neither shape matched the
        // server. Read `suggestions` first, fall back to top-level fields for
        // safety, and ignore the old `suggested` key entirely.
        const s = res.suggestions || res || {};
        if (s.mission) state.mission = s.mission;
        if (s.voice) {
            // matchVoiceOption does case-insensitive contains-matching, so
            // a server return of "Professional" maps to "Professional +
            // authoritative" instead of incorrectly falling into "Other".
            const matched = matchVoiceOption(s.voice, VOICE_OPTIONS);
            if (matched.option) state.voice = matched.option;
            else { state.voice = 'Other'; state.voiceCustom = matched.custom; }
        }
        // No-AI-key scans return generic stub text plus a warning. Show it —
        // otherwise boilerplate silently lands in the mission field looking
        // like it came from the user's real website.
        if (res.warning) {
            state.scanError = res.warning + " We've filled in generic examples below — please rewrite them for your brand.";
        }
    } catch (e) {
        state.scanError = "Couldn't read that site — try pasting the mission manually.";
    } finally {
        state.scanning = false;
        render();
    }
}

async function advanceFromStep1() {
    if (!state.brandName || !state.mission || !state.voice) return;
    state.savingProfile = true;
    try {
        await client.updateProfile({
            mission: state.mission,
            voice: state.voice === 'Other' ? state.voiceCustom : state.voice,
        });
    } catch (_) { /* non-fatal — wizard continues */ }
    // Phase 41 — name the workspace. Onboarding never captured the brand
    // name before, so the sidebar stayed "Heist". Rename the active brand
    // (id from localStorage, falling back to the first brand). Non-fatal.
    if (state.brandName && state.brandName.trim()) {
        try {
            let brandId = null;
            try { brandId = localStorage.getItem('heist_active_brand'); } catch (_) {}
            if (!brandId) {
                const br = await client.getBrands();
                brandId = br && br.brands && br.brands[0] && br.brands[0].id;
            }
            if (brandId) await client.renameBrand(brandId, state.brandName.trim());
        } catch (_) { /* non-fatal — wizard continues */ }
    }
    state.savingProfile = false;
    track('setup_wizard_step', { step: 1, action: 'completed' });
    state.step = 1;
    state.view = 'fork';
    persistView(null);
    render();
}

async function saveKey() {
    if (!state.apiKey || state.savingKey) return;
    state.savingKey = true;
    state.keyError = null;
    state.keyOk = false;
    render();
    try {
        await client.updateAiSettings({ mode: 'byok', provider: state.provider, api_key: state.apiKey });
        state.keyOk = true;
        state.savingKey = false;
        if (window.__auth__) window.__auth__.has_ai_key = true;
        render();
        // After a brief moment of success, finalise the heist path.
        setTimeout(() => { finishHeistPath(); }, 700);
    } catch (e) {
        state.savingKey = false;
        state.keyError = e.message || "Key didn't work. Double-check and try again.";
        render();
    }
}

async function handleSkip() {
    track('setup_skipped', { step: (state.step || 0) + 1 });
    try { await client.setupSkipped(); } catch (_) {}
    // Mirror the server-side setup_skipped_at flag locally so any
    // heist:auth-changed re-decision this session doesn't refire the wizard.
    if (window.__auth__) window.__auth__.setup_skipped = true;
    clearAll();
    unmount();
}

// Phase 40 — Step 2's three finish handlers used to terminate the wizard.
// Now they stash the chosen path + args and advance to Step 3 ("What you
// can make"). The actual completeSetup + route happens in finishStep3()
// after the user clicks "Got it" on the new summary screen.
function stashFinishPath(path, args = {}) {
    state.pendingFinishPath = path;
    state.pendingFinishArgs = args;
    try {
        localStorage.setItem(STORAGE_FINISH_PATH, path);
        localStorage.setItem(STORAGE_FINISH_ARGS, JSON.stringify(args || {}));
    } catch (_) {}
}

function readFinishPath() {
    try {
        const path = localStorage.getItem(STORAGE_FINISH_PATH) || null;
        const argsRaw = localStorage.getItem(STORAGE_FINISH_ARGS) || '{}';
        const args = JSON.parse(argsRaw);
        return { path, args };
    } catch (_) { return { path: null, args: {} }; }
}

function advanceToStep3(path, args) {
    stashFinishPath(path, args);
    track('setup_wizard_step', { step: 2, action: 'completed', path });
    state.step = 2;
    persistStep(2);
    render();
}

async function finishHeistPath() {
    advanceToStep3('byok', {});
}

async function finishMcpPath(platformId, externalUrl) {
    advanceToStep3('mcp', { platformId, externalUrl });
}

async function finishJustTryPath() {
    advanceToStep3('try', {});
}

// Phase 40 — single terminal handler called from Step 3's "Got it" button.
// Replays the chosen Step 2 finish path against the live destination state.
async function finishStep3(destOverride) {
    const { path, args } = readFinishPath();
    track('setup_completed', { path: path || 'unknown' });
    try { await client.completeSetup(); } catch (_) {}
    if (window.__auth__) window.__auth__.onboarding_completed = true;
    clearAll();
    unmount();
    // Phase 41 — an explicit next-action button (e.g. "Plan my month") wins
    // over the Step-2 path-based default routing.
    if (destOverride) { window.location.hash = destOverride; return; }
    if (path === 'mcp') {
        const platformId = args && args.platformId;
        const externalUrl = args && args.externalUrl;
        const platformQuery = platformId ? `?platform=${encodeURIComponent(platformId)}` : '';
        window.location.hash = '#connectors' + platformQuery;
        if (externalUrl) {
            try { window.open(externalUrl, '_blank', 'noopener'); } catch (_) {}
        }
    } else if (path === 'try') {
        try {
            sessionStorage.setItem('brain.activeTab', 'chat');
            sessionStorage.setItem('brain.seedMessage', "I just signed up for Heist. Help me figure out what to post first — ask me a couple of quick questions about my brand, then suggest one or two posts I can publish today.");
        } catch (_) {}
        window.location.hash = '#memory?tab=chat&new=1';
    } else {
        // 'byok' default — drop the user into Content Studio. The coachmark
        // tour no longer auto-starts here (B1 F-UX-2: it stacked on top of
        // the Studio and blocked the Generate button). Re-entry: the
        // per-page "Tour this page" chip, the bell's tour_reminder
        // notification, and #?tour=1.
        window.location.hash = '#create?prefill=sample';
    }
}

export function isSetupWizardOpen() {
    return !!overlay;
}
